import React, { FC } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import ExternalLink from 'components/external-link';
import IconFacebook from 'components/icons/facebook';
import IconInstagram from 'components/icons/instagram';
import IconTwitter from 'components/icons/twitter';
import IconYoutube from 'components/icons/youtube';
import LanguageSelector from 'components/language-selector';
import Link from 'components/link';
import useRoutes from 'hooks/use-routes';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { routes } from 'utils/routing';

import { Row } from './shared';
import { IFooterProps } from './types';
import { useFeatureFooter } from './use-feature-footer';

const FooterContainer = styled.footer`
  background-color: ${Styles.color.grey.six};
  color: ${Styles.color.black};
  padding: 0 4.5rem;
  width: 100%;

  ${Styles.mobile`
    display: none;
  `}
`;

const HorizontalRule = styled.hr`
  background-color: ${Styles.color.grey.two};
  border: 0;
  height: 1px;
  opacity: 0.1;
`;

const MainLinksContainer = styled.div`
  ${Styles.mobile`
    padding: 1em 0;
    flex-direction: column;
  `}

  display: flex;
  padding: 3em 0;
  flex-direction: row;
  flex-wrap: no-wrap;
  justify-content: space-between;
`;

const CreditLinksContainer = styled(MainLinksContainer)`
  ${Styles.mobile`
    padding: 1em 0;
  `}

  padding: 1em 0;
  margin-bottom: 2em;
`;

const ColumnLinksContainer = styled.div`
  font-family: ${Styles.fontFamily.base};
  display: block;
  width: 33.33%;
  padding-right: 1rem;

  ${Styles.mobile`
    display:block;
    margin-bottom: 1rem
  `}
`;

const SocialColumnLinksContainer = styled(ColumnLinksContainer)`
  ${Styles.mobile`
    text-align: center;
  `}
`;

const ColumnLinksContainerFlex = styled(ColumnLinksContainer)`
  display: flex;
  align-items: flex-end;
  min-width: auto;
`;

const LinkItem = styled(Link)`
  text-decoration: none;
  color: ${Styles.color.primary};
  font-size: 1rem;

  &:hover {
    cursor: pointer;
    color: ${Styles.color.primary};
    text-decoration: underline;
  }

  // Override global outline color here for contrast ratio
  :focus {
    outline-color: ${Styles.color.black};
  }
`;

const ExternalLinkItem = styled(ExternalLink)`
  text-decoration: none;
  color: ${Styles.color.black};
  font-size: 1rem;

  &:hover {
    cursor: pointer;
    text-decoration: underline;
    color: ${Styles.color.black};
  }

  // Override global outline color here for contrast ratio
  :focus {
    outline-color: ${Styles.color.white};
  }
`;

const SecondaryLinkItem = styled(LinkItem)`
  color: ${Styles.color.black};
  padding-bottom: 0.5rem;
  display: block;
  &:hover {
    color: ${Styles.color.black};
  }
`;

const ExternalSecondaryLinkItem = styled(ExternalLinkItem)`
  color: ${Styles.color.black};
  padding-bottom: 0.5rem;
  display: block;
  &:hover {
    color: ${Styles.color.black};
  }
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;

  & > * {
    margin: 0 0.5rem;
  }

  ${Styles.desktop`
    justify-content: flex-start;
    margin-left: -15px;
  `}
`;

const IconBubble = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 15px;
  display: flex;
  justify-content: center;
  align-items: center;

  > * {
    width: 50%;
    height: 50%;
    fill: ${Styles.color.black};
  }

  &:hover {
    background-color: ${Styles.color.primaryHover};
    cursor: pointer;

    > * {
      fill: ${Styles.color.white};
    }
  }
`;

const Footer: FC<IFooterProps> = () => {
  const { formatMessage } = useIntl();
  const { getLocalizedRouteForPath } = useRoutes();
  const enableDiagnosticTools = useFlag(LaunchDarklyFlag.ENABLE_DIAGNOSTIC_TOOLS);

  const enableSanityFooter = useFlag(LaunchDarklyFlag.ENABLE_SANITY_FOOTER);

  const { featureFooter, featureFooterLoading } = useFeatureFooter();

  if (enableSanityFooter) {
    return featureFooterLoading ? null : (
      <FooterContainer role="contentinfo">
        {featureFooter?.rows?.map((row, index) => row && <Row key={row._key ?? index} {...row} />)}
      </FooterContainer>
    );
  }

  return (
    <FooterContainer role="contentinfo">
      <MainLinksContainer>
        <ColumnLinksContainer>
          <b>{formatMessage(messages.careers)} </b>
          <div>{formatMessage(messages.comeJoinTheTeam)}</div>
          <LinkItem to={getLocalizedRouteForPath('join-our-team')}>
            {formatMessage(messages.browseOpportunities)}
          </LinkItem>
        </ColumnLinksContainer>
        <ColumnLinksContainer>
          <b>{formatMessage(messages.community)}</b>
          <div>{formatMessage(messages.makeATrueDifference)}</div>
          <LinkItem to={getLocalizedRouteForPath('community')}>
            {formatMessage(messages.learnMore)}
          </LinkItem>
        </ColumnLinksContainer>
        <ColumnLinksContainer>
          <b>{formatMessage(messages.findATims)} </b>
          <div>{formatMessage(messages.weCantWaitToServeYou)}</div>
          <LinkItem to={getLocalizedRouteForPath('store-locator')}>
            {formatMessage(messages.storeLocator)}
          </LinkItem>
        </ColumnLinksContainer>
      </MainLinksContainer>
      <HorizontalRule />
      <MainLinksContainer>
        <ColumnLinksContainer>
          <SecondaryLinkItem to={getLocalizedRouteForPath('franchising')}>
            {formatMessage(messages.franchising)}
          </SecondaryLinkItem>
          <ExternalSecondaryLinkItem href="http://www.rbi.com/Investor-Home">
            {formatMessage(messages.investors)}
          </ExternalSecondaryLinkItem>
          <SecondaryLinkItem to={getLocalizedRouteForPath('contact-us')}>
            {formatMessage(messages.contactUs)}
          </SecondaryLinkItem>
          <SecondaryLinkItem to={getLocalizedRouteForPath('frequently-asked-questions')}>
            {formatMessage(messages.faq)}
          </SecondaryLinkItem>
        </ColumnLinksContainer>
        <ColumnLinksContainer>
          <SecondaryLinkItem to={getLocalizedRouteForPath('privacy-policy')}>
            {formatMessage(messages.privacyPolicy)}
          </SecondaryLinkItem>
          <SecondaryLinkItem to={getLocalizedRouteForPath('terms-of-use')}>
            {formatMessage(messages.termsOfService)}
          </SecondaryLinkItem>
          <SecondaryLinkItem to={getLocalizedRouteForPath('trademarks')}>
            {formatMessage(messages.trademarks)}
          </SecondaryLinkItem>
          <SecondaryLinkItem to={getLocalizedRouteForPath('accessibility')}>
            {formatMessage(messages.accessibility)}
          </SecondaryLinkItem>
          {enableDiagnosticTools && (
            <SecondaryLinkItem to={routes.diagnostics}>
              {formatMessage(messages.diagnostics)}
            </SecondaryLinkItem>
          )}
        </ColumnLinksContainer>
        <SocialColumnLinksContainer>
          <b>{formatMessage(messages.connectWithUs)}</b>
          <IconContainer>
            <ExternalLink href="http://facebook.com/TimHortons/">
              <IconBubble>
                <IconFacebook aria-hidden="true" />
              </IconBubble>
            </ExternalLink>
            <ExternalLink href="https://twitter.com/timhortons/">
              <IconBubble>
                <IconTwitter aria-hidden="true" />
              </IconBubble>
            </ExternalLink>
            <ExternalLink href="https://www.youtube.com/user/TimHortons">
              <IconBubble>
                <IconYoutube aria-hidden="true" />
              </IconBubble>
            </ExternalLink>
            <ExternalLink href="https://www.instagram.com/TimHortons/">
              <IconBubble>
                <IconInstagram aria-hidden="true" />
              </IconBubble>
            </ExternalLink>
          </IconContainer>
        </SocialColumnLinksContainer>
      </MainLinksContainer>
      <HorizontalRule />
      <CreditLinksContainer>
        <ColumnLinksContainerFlex>
          {formatMessage(messages.copyrightTimHortons)}
        </ColumnLinksContainerFlex>
        <ColumnLinksContainerFlex>
          <LanguageSelector color={Styles.color.black} />
        </ColumnLinksContainerFlex>
      </CreditLinksContainer>
    </FooterContainer>
  );
};

export default Footer;
const messages = defineMessages({
  copyrightTimHortons: {
    id: 'copyrightTimHortons',
    defaultMessage: ' TM & © Tim Hortons, 2020',
    description: 'No description'
  },

  connectWithUs: {
    id: 'connectWithUs',
    defaultMessage: 'Connect with Us',
    description: 'No description'
  },

  diagnostics: {
    id: 'diagnostics',
    defaultMessage: 'Diagnostics',
    description: 'No description'
  },

  accessibility: {
    id: 'accessibility',
    defaultMessage: 'Accessibility',
    description: 'No description'
  },

  trademarks: {
    id: 'trademarks',
    defaultMessage: 'Trademarks Notice',
    description: 'No description'
  },

  termsOfService: {
    id: 'termsOfService',
    defaultMessage: 'Terms of Service',
    description: 'No description'
  },

  privacyPolicy: {
    id: 'privacyPolicy',
    defaultMessage: 'Privacy Policy',
    description: 'No description'
  },

  faq: {
    id: 'faq',
    defaultMessage: 'Frequently asked questions',
    description: 'No description'
  },

  contactUs: {
    id: 'contactUs',
    defaultMessage: 'Contact Us',
    description: 'No description'
  },

  investors: {
    id: 'investors',
    defaultMessage: 'Investors',
    description: 'No description'
  },

  franchising: {
    id: 'franchising',
    defaultMessage: 'Franchising',
    description: 'No description'
  },

  storeLocator: {
    id: 'storeLocator',
    defaultMessage: 'Store Locator',
    description: 'No description'
  },

  weCantWaitToServeYou: {
    id: 'weCantWaitToServeYou',
    defaultMessage: "We can't wait to serve you",
    description: 'No description'
  },

  findATims: {
    id: 'findATims',
    defaultMessage: 'Find a Tim Hortons',
    description: 'No description'
  },

  learnMore: {
    id: 'learnMore',
    defaultMessage: 'Learn More',
    description: 'No description'
  },

  makeATrueDifference: {
    id: 'makeATrueDifference',
    defaultMessage: 'Make a true difference',
    description: 'No description'
  },

  community: {
    id: 'community',
    defaultMessage: 'Community',
    description: 'No description'
  },

  browseOpportunities: {
    id: 'browseOpportunities',
    defaultMessage: 'Browse Opportunities',
    description: 'No description'
  },

  comeJoinTheTeam: {
    id: 'comeJoinTheTeam',
    defaultMessage: 'Come join the team',
    description: 'No description'
  },

  careers: {
    id: 'careers',
    defaultMessage: 'Careers',
    description: 'No description'
  }
});
